import { Box, useTheme } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import React from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";

import { ProtectedRoute } from "@/components/auth";
import { SIDEBAR_WIDTH } from "@/components/layout/constants";
import { Navbar } from "@/components/layout/Navbar";
import { Sidebar } from "@/components/layout/Sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { getDateFnsLocale } from "@/i18n/dateFnsLocale";
import CommandCenterPage from "@/pages/admin/command-center/CommandCenterPage";
import CategoryPage from "@/pages/admin/configuration/categories/CategoryPage";
import { Configs, EditConfig } from "@/pages/admin/configuration/configs";
import MobileLinks from "@/pages/admin/configuration/mobile-links/MobileLinks";
import DataConsistency from "@/pages/admin/data/data-consistency/DataConsistency";
import { ETLRunDetails, ETLRunReport, ETLRuns } from "@/pages/admin/data/etl-runs";
import ShopperDetailsPage from "@/pages/admin/data/shopper-details/ShopperDetails";
import Simulator from "@/pages/admin/development/simulator/Debug";
import ClickAndCollectReport from "@/pages/admin/experimental/click-and-collect/ClickAndCollectReport";
import ComparisonAnalytics from "@/pages/admin/experimental/comparison-analytics/ComparisonAnalytics";
import { Inventory, ProductDetail } from "@/pages/admin/experimental/inventory";
import SessionsReport from "@/pages/admin/experimental/sessions/SessionsReport";
import CentralRfidActivity from "@/pages/admin/hardware/central-rfid-activity/CentralRfidActivity";
import {
  DeviceHistory,
  Devices,
  RFIDReaderAdd,
  RFIDReaderDetails,
  RFIDReaderEdit,
  RFIDReaders,
} from "@/pages/admin/hardware/devices";
import { ApiKeys } from "@/pages/admin/integrations/api-keys";
import SsoProviderEditor from "@/pages/admin/integrations/sso/SsoProviderEditor";
import SsoProviders from "@/pages/admin/integrations/sso/SsoProviders";
import { CreateWebhook, EditWebhook, Webhooks } from "@/pages/admin/integrations/webhooks";
import { CustomerNotes, Customers } from "@/pages/admin/organization/customers";
import {
  EditChangingRooms,
  StoreDetails,
  StoreForm,
  Stores,
} from "@/pages/admin/organization/stores";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import Login from "@/pages/auth/Login";
import ResetPassword from "@/pages/auth/ResetPassword";
import SsoCallback from "@/pages/auth/SsoCallback";
import CantFindReport from "@/pages/reports/cant-find/CantFindReport";
import ContinuousReadsReport from "@/pages/reports/continuous-reads/ContinuousReadsReport";
import MissedReport from "@/pages/reports/missed/MissedReport";
import OutOfStockReport from "@/pages/reports/out-of-stock/OutOfStockReport";
import PopularProductsReport from "@/pages/reports/popular-products/PopularProductsReport";
import SuspiciousActivityReport from "@/pages/reports/suspicious-activity/SuspiciousActivityReport";
import { MultiStoreOverview } from "@/pages/store-insights/comparison/MultiStoreOverview";
import GroupComparison from "@/pages/store-insights/group-comparison/GroupComparison";
import FittingRoomsLiveView from "@/pages/store-insights/live-experience/live-view/FittingRoomsLiveView";
import StorePerformance from "@/pages/store-insights/performance/StorePerformance";
import { Help } from "@/pages/utilities/help";
import Feedback from "@/pages/utilities/help/feedback/Feedback/Feedback";
import { Profile } from "@/pages/utilities/profile";
import { Settings } from "@/pages/utilities/settings";
import { getLegacyEditRedirectPath, getLegacyRedirectPath } from "@/routes/legacyRedirect";
import { paths } from "@/routes/paths";
import { userHasPermission } from "@/utils/permissionManager";
import { UserRole } from "@/utils/roleManager";

const PermissionRoute = React.memo<{
  element: React.ReactElement;
  minimumRole: UserRole;
  shouldNavigate?: boolean;
}>(({ element, minimumRole, shouldNavigate = true }) => {
  const { user } = useAuth();

  if (!user || !userHasPermission(user.role, minimumRole)) {
    return shouldNavigate ? (
      <Navigate to={paths.storeInsights.fittingRoomsLiveView} replace />
    ) : null;
  }

  return element;
});
PermissionRoute.displayName = "PermissionRoute";

const LegacyRedirect: React.FC<{ fromPrefix: string; toPrefix: string }> = ({
  fromPrefix,
  toPrefix,
}) => {
  const location = useLocation();
  return (
    <Navigate
      replace
      to={{
        pathname: getLegacyRedirectPath(location.pathname, fromPrefix, toPrefix),
        search: location.search,
        hash: location.hash,
      }}
    />
  );
};

const LegacyStoreEditRedirect: React.FC = () => {
  const location = useLocation();
  const { storeId } = useParams<{ storeId: string }>();

  return (
    <Navigate
      replace
      to={{
        pathname: storeId
          ? getLegacyEditRedirectPath(paths.admin.stores, storeId)
          : paths.admin.stores,
        search: location.search,
        hash: location.hash,
      }}
    />
  );
};

const Layout: React.FC = () => {
  const location = useLocation();
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const dateLocale = React.useMemo(
    () => getDateFnsLocale(i18n.resolvedLanguage || i18n.language),
    [i18n.resolvedLanguage, i18n.language],
  );

  React.useEffect(() => {
    const main = document.querySelector<HTMLElement>(".layout-main");
    main?.scrollTo({ top: 0, left: 0, behavior: "auto" });
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  const nestedRoutes = (
    children: React.ReactElement,
    minimumRole: UserRole,
    shouldNavigate = true,
  ) => (
    <PermissionRoute element={children} minimumRole={minimumRole} shouldNavigate={shouldNavigate} />
  );

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      localeText={{
        cancelButtonLabel: t("common.cancel"),
        okButtonLabel: t("common.ok"),
        clearButtonLabel: t("common.clear"),
      }}
      adapterLocale={dateLocale}
    >
      <Box
        className="flex flex-col h-screen"
        sx={{ "--sidebar-width": SIDEBAR_WIDTH }}
        data-rtl={theme.direction === "rtl"}
      >
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/login/sso/callback" element={<SsoCallback />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/*"
            element={
              <ProtectedRoute>
                <Box className="h-full">
                  <Navbar />
                  <Box sx={{ display: "flex", position: "relative", pt: 8 }}>
                    <Box
                      className="sidebar-container"
                      sx={{ display: { xs: "none", xl: "block" } }}
                    >
                      <Sidebar />
                    </Box>
                    <Box
                      component="main"
                      className="layout-main"
                      sx={{
                        width: { xs: "100%", xl: "calc(100% - var(--sidebar-width))" },
                        ml: { xl: "var(--sidebar-width)" },
                      }}
                    >
                      <Box
                        sx={{ maxWidth: 1600, mx: "auto", width: "100%", px: { xs: 0, lg: 1.5 } }}
                      >
                        <Routes>
                          <Route
                            path="/"
                            element={<Navigate to={paths.storeInsights.performance} replace />}
                          />
                          <Route
                            path="insights"
                            element={
                              <LegacyRedirect
                                fromPrefix={paths.storeInsights.root}
                                toPrefix={paths.storeInsights.performance}
                              />
                            }
                          />
                          <Route
                            path="insights/performance"
                            element={nestedRoutes(
                              <StorePerformance />,
                              UserRole.STORE_ADMIN,
                              false,
                            )}
                          />
                          <Route
                            path="insights/comparison"
                            element={nestedRoutes(
                              <MultiStoreOverview />,
                              UserRole.CUSTOMER_ADMIN,
                              false,
                            )}
                          />
                          <Route
                            path="insights/group-comparison"
                            element={nestedRoutes(
                              <GroupComparison />,
                              UserRole.CUSTOMER_ADMIN,
                              false,
                            )}
                          />
                          <Route
                            path="insights/live/fitting-rooms"
                            element={nestedRoutes(<FittingRoomsLiveView />, UserRole.ASSOCIATE)}
                          />

                          <Route
                            path="reports/sessions"
                            element={nestedRoutes(<SessionsReport />, UserRole.STORE_ADMIN, false)}
                          />
                          <Route
                            path="reports/cant-find"
                            element={nestedRoutes(<CantFindReport />, UserRole.STORE_ADMIN, false)}
                          />
                          <Route
                            path="reports/missed"
                            element={nestedRoutes(<MissedReport />, UserRole.STORE_ADMIN, false)}
                          />
                          <Route
                            path="reports/out-of-stock"
                            element={nestedRoutes(
                              <OutOfStockReport />,
                              UserRole.STORE_ADMIN,
                              false,
                            )}
                          />
                          <Route
                            path="reports/continuous-reads"
                            element={nestedRoutes(
                              <ContinuousReadsReport />,
                              UserRole.STORE_ADMIN,
                              false,
                            )}
                          />
                          <Route
                            path="reports/popular-products"
                            element={nestedRoutes(
                              <PopularProductsReport />,
                              UserRole.STORE_ADMIN,
                              false,
                            )}
                          />
                          <Route
                            path="reports/suspicious-activity"
                            element={nestedRoutes(
                              <SuspiciousActivityReport />,
                              UserRole.STORE_ADMIN,
                              false,
                            )}
                          />
                          <Route
                            path="reports/click-and-collect"
                            element={nestedRoutes(
                              <ClickAndCollectReport />,
                              UserRole.STORE_ADMIN,
                              false,
                            )}
                          />

                          <Route
                            path="admin/command-center"
                            element={nestedRoutes(<CommandCenterPage />, UserRole.CRAVE_EMPLOYEE)}
                          />
                          <Route
                            path="admin/organization/customers/*"
                            element={nestedRoutes(
                              <Routes>
                                <Route path="/" element={<Customers />} />
                                <Route path="notes/:customerId" element={<CustomerNotes />} />
                              </Routes>,
                              UserRole.CRAVE_EMPLOYEE,
                            )}
                          />
                          <Route
                            path="admin/organization/stores/*"
                            element={nestedRoutes(
                              <Routes>
                                <Route path="/" element={<Stores />} />
                                <Route
                                  path="add/:customerId/:customerName"
                                  element={<StoreForm />}
                                />
                                <Route path=":storeId" element={<StoreDetails />} />
                                <Route path="edit/:storeId" element={<StoreForm />} />
                                <Route path="edit-rooms/:storeId" element={<EditChangingRooms />} />
                              </Routes>,
                              UserRole.CRAVE_EMPLOYEE,
                            )}
                          />
                          <Route
                            path="admin/hardware/devices/*"
                            element={nestedRoutes(
                              <Routes>
                                <Route path="/" element={<Devices />} />
                                <Route path="history/:deviceId" element={<DeviceHistory />} />
                              </Routes>,
                              UserRole.CRAVE_EMPLOYEE,
                            )}
                          />
                          <Route
                            path="admin/hardware/rfid-readers/*"
                            element={nestedRoutes(
                              <Routes>
                                <Route path="/" element={<RFIDReaders />} />
                                <Route path="add" element={<RFIDReaderAdd />} />
                                <Route path=":readerName" element={<RFIDReaderDetails />} />
                                <Route path="edit/:readerName" element={<RFIDReaderEdit />} />
                              </Routes>,
                              UserRole.CRAVE_EMPLOYEE,
                            )}
                          />
                          <Route
                            path="admin/hardware/central-rfid-activity"
                            element={nestedRoutes(
                              <CentralRfidActivity />,
                              UserRole.CRAVE_EMPLOYEE,
                              false,
                            )}
                          />
                          <Route
                            path="admin/data/etl-runs/*"
                            element={nestedRoutes(
                              <Routes>
                                <Route path="/" element={<ETLRuns />} />
                                <Route path=":jobId/report" element={<ETLRunReport />} />
                                <Route path=":jobId" element={<ETLRunDetails />} />
                              </Routes>,
                              UserRole.CRAVE_EMPLOYEE,
                            )}
                          />
                          <Route
                            path="admin/data/data-consistency"
                            element={nestedRoutes(
                              <DataConsistency />,
                              UserRole.CRAVE_EMPLOYEE,
                              false,
                            )}
                          />
                          <Route
                            path="admin/data/shopper-details"
                            element={nestedRoutes(<ShopperDetailsPage />, UserRole.CRAVE_EMPLOYEE)}
                          />
                          <Route
                            path="admin/integrations/webhooks/*"
                            element={nestedRoutes(
                              <Routes>
                                <Route path="/" element={<Webhooks />} />
                                <Route path="create" element={<CreateWebhook />} />
                                <Route path="create/:customerId" element={<CreateWebhook />} />
                                <Route path="edit/:webhookId" element={<EditWebhook />} />
                              </Routes>,
                              UserRole.CRAVE_EMPLOYEE,
                            )}
                          />
                          <Route
                            path="admin/integrations/api-keys"
                            element={nestedRoutes(<ApiKeys />, UserRole.CRAVE_ADMIN)}
                          />
                          <Route
                            path="admin/configuration/configs/*"
                            element={nestedRoutes(
                              <Routes>
                                <Route path="/" element={<Configs />} />
                                <Route path="edit/:configId" element={<EditConfig />} />
                              </Routes>,
                              UserRole.CRAVE_ADMIN,
                            )}
                          />
                          <Route
                            path="admin/configuration/mobile-links"
                            element={nestedRoutes(<MobileLinks />, UserRole.CRAVE_EMPLOYEE)}
                          />
                          <Route
                            path="admin/configuration/categories/:section"
                            element={nestedRoutes(<CategoryPage />, UserRole.CRAVE_EMPLOYEE)}
                          />
                          <Route
                            path="admin/configuration/categories"
                            element={
                              <Navigate
                                to={`${paths.admin.categories}/customer-categories`}
                                replace
                              />
                            }
                          />
                          <Route
                            path="admin/development/simulator"
                            element={nestedRoutes(<Simulator />, UserRole.CRAVE_EMPLOYEE)}
                          />
                          <Route
                            path="admin/experimental/sessions"
                            element={
                              <LegacyRedirect
                                fromPrefix="/admin/experimental/sessions"
                                toPrefix={paths.reports.sessions}
                              />
                            }
                          />
                          <Route
                            path="admin/experimental/click-and-collect"
                            element={
                              <LegacyRedirect
                                fromPrefix="/admin/experimental/click-and-collect"
                                toPrefix={paths.reports.clickAndCollect}
                              />
                            }
                          />
                          <Route
                            path="admin/experimental/inventory/*"
                            element={nestedRoutes(
                              <Routes>
                                <Route path="/" element={<Inventory />} />
                                <Route path="product/:productId" element={<ProductDetail />} />
                              </Routes>,
                              UserRole.CRAVE_EMPLOYEE,
                            )}
                          />
                          <Route
                            path="admin/experimental/comparison-analytics"
                            element={nestedRoutes(
                              <ComparisonAnalytics />,
                              UserRole.CRAVE_EMPLOYEE,
                              false,
                            )}
                          />

                          <Route
                            path="store-insights/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/store-insights"
                                toPrefix={paths.storeInsights.root}
                              />
                            }
                          />
                          <Route
                            path="users/*"
                            element={nestedRoutes(<Settings />, UserRole.STORE_ADMIN)}
                          />
                          <Route path="faq/*" element={<Help />} />
                          <Route path="feedback" element={<Feedback />} />
                          <Route
                            path="profile/*"
                            element={nestedRoutes(<Profile />, UserRole.ASSOCIATE)}
                          />

                          <Route
                            path="utilities/settings/user-management/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/utilities/settings/user-management"
                                toPrefix={paths.utilities.userManagement}
                              />
                            }
                          />
                          <Route
                            path="utilities/settings/*"
                            element={nestedRoutes(<Settings />, UserRole.STORE_ADMIN)}
                          />
                          <Route
                            path="utilities/help/feedback/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/utilities/help/feedback"
                                toPrefix={paths.utilities.feedback}
                              />
                            }
                          />
                          <Route
                            path="utilities/help"
                            element={
                              <LegacyRedirect
                                fromPrefix="/utilities/help"
                                toPrefix={paths.utilities.faq}
                              />
                            }
                          />
                          <Route
                            path="utilities/help/faq/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/utilities/help/faq"
                                toPrefix={paths.utilities.faq}
                              />
                            }
                          />
                          <Route path="utilities/help/*" element={<Help />} />
                          <Route
                            path="utilities/profile/*"
                            element={nestedRoutes(<Profile />, UserRole.ASSOCIATE)}
                          />

                          <Route
                            path="live-view/fitting-rooms"
                            element={
                              <Navigate to={paths.storeInsights.fittingRoomsLiveView} replace />
                            }
                          />
                          <Route
                            path="live-view"
                            element={
                              <Navigate to={paths.storeInsights.fittingRoomsLiveView} replace />
                            }
                          />
                          <Route
                            path="store-performance"
                            element={
                              <LegacyRedirect
                                fromPrefix="/store-performance"
                                toPrefix={paths.storeInsights.performance}
                              />
                            }
                          />
                          <Route
                            path="store-comparison"
                            element={
                              <LegacyRedirect
                                fromPrefix="/store-comparison"
                                toPrefix={paths.storeInsights.comparison}
                              />
                            }
                          />
                          <Route
                            path="group-comparison"
                            element={
                              <LegacyRedirect
                                fromPrefix="/group-comparison"
                                toPrefix={paths.storeInsights.groupComparison}
                              />
                            }
                          />
                          <Route
                            path="comparison-analytics"
                            element={
                              <LegacyRedirect
                                fromPrefix="/comparison-analytics"
                                toPrefix={paths.admin.comparisonAnalytics}
                              />
                            }
                          />
                          <Route
                            path="admin/integrations/sso/*"
                            element={nestedRoutes(
                              <Routes>
                                <Route path="/" element={<SsoProviders />} />
                                <Route
                                  path=":customerId/:providerId"
                                  element={<SsoProviderEditor />}
                                />
                              </Routes>,
                              UserRole.CRAVE_EMPLOYEE,
                            )}
                          />
                          <Route
                            path="command-center"
                            element={
                              <LegacyRedirect
                                fromPrefix="/command-center"
                                toPrefix={paths.admin.commandCenter}
                              />
                            }
                          />
                          <Route
                            path="customers/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/customers"
                                toPrefix={paths.admin.customers}
                              />
                            }
                          />
                          <Route
                            path="stores/*"
                            element={
                              <LegacyRedirect fromPrefix="/stores" toPrefix={paths.admin.stores} />
                            }
                          />
                          <Route path="store/:storeId/edit" element={<LegacyStoreEditRedirect />} />
                          <Route
                            path="devices/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/devices"
                                toPrefix={paths.admin.devices}
                              />
                            }
                          />
                          <Route
                            path="rfid-readers/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/rfid-readers"
                                toPrefix={paths.admin.rfidReaders}
                              />
                            }
                          />
                          <Route
                            path="central-rfid-activity"
                            element={
                              <LegacyRedirect
                                fromPrefix="/central-rfid-activity"
                                toPrefix={paths.admin.centralRfidActivity}
                              />
                            }
                          />
                          <Route
                            path="etl-runs/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/etl-runs"
                                toPrefix={paths.admin.etlRuns}
                              />
                            }
                          />
                          <Route
                            path="data-consistency"
                            element={
                              <LegacyRedirect
                                fromPrefix="/data-consistency"
                                toPrefix={paths.admin.dataConsistency}
                              />
                            }
                          />
                          <Route
                            path="shopper-details"
                            element={
                              <LegacyRedirect
                                fromPrefix="/shopper-details"
                                toPrefix={paths.admin.shopperDetails}
                              />
                            }
                          />
                          <Route
                            path="webhooks/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/webhooks"
                                toPrefix={paths.admin.webhooks}
                              />
                            }
                          />
                          <Route
                            path="api-keys"
                            element={
                              <LegacyRedirect
                                fromPrefix="/api-keys"
                                toPrefix={paths.admin.apiKeys}
                              />
                            }
                          />
                          <Route
                            path="configs/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/configs"
                                toPrefix={paths.admin.configs}
                              />
                            }
                          />
                          <Route
                            path="management/mobileLink"
                            element={
                              <LegacyRedirect
                                fromPrefix="/management/mobileLink"
                                toPrefix={paths.admin.mobileLinks}
                              />
                            }
                          />
                          <Route
                            path="mobileLink"
                            element={
                              <LegacyRedirect
                                fromPrefix="/mobileLink"
                                toPrefix={paths.admin.mobileLinks}
                              />
                            }
                          />
                          <Route
                            path="categories/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/categories"
                                toPrefix={paths.admin.categories}
                              />
                            }
                          />
                          <Route
                            path="customer-category"
                            element={
                              <LegacyRedirect
                                fromPrefix="/customer-category"
                                toPrefix={`${paths.admin.categories}/customer-categories`}
                              />
                            }
                          />
                          <Route
                            path="category-mapping"
                            element={
                              <LegacyRedirect
                                fromPrefix="/category-mapping"
                                toPrefix={`${paths.admin.categories}/category-mapping`}
                              />
                            }
                          />
                          <Route
                            path="crave-categories"
                            element={
                              <LegacyRedirect
                                fromPrefix="/crave-categories"
                                toPrefix={`${paths.admin.categories}/crave-categories`}
                              />
                            }
                          />
                          <Route
                            path="debug"
                            element={
                              <LegacyRedirect
                                fromPrefix="/debug"
                                toPrefix={paths.admin.simulator}
                              />
                            }
                          />
                          <Route
                            path="simulator"
                            element={
                              <LegacyRedirect
                                fromPrefix="/simulator"
                                toPrefix={paths.admin.simulator}
                              />
                            }
                          />
                          <Route
                            path="inventory/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/inventory"
                                toPrefix={paths.admin.inventory}
                              />
                            }
                          />
                          <Route
                            path="settings/user-management/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/settings/user-management"
                                toPrefix={paths.utilities.userManagement}
                              />
                            }
                          />
                          <Route
                            path="settings/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/settings"
                                toPrefix={paths.utilities.userManagement}
                              />
                            }
                          />
                          <Route
                            path="help/feedback/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/help/feedback"
                                toPrefix={paths.utilities.feedback}
                              />
                            }
                          />
                          <Route
                            path="help/faq/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/help/faq"
                                toPrefix={paths.utilities.faq}
                              />
                            }
                          />
                          <Route
                            path="help/*"
                            element={
                              <LegacyRedirect fromPrefix="/help" toPrefix={paths.utilities.faq} />
                            }
                          />
                          <Route
                            path="profile/*"
                            element={
                              <LegacyRedirect
                                fromPrefix="/profile"
                                toPrefix={paths.utilities.profile}
                              />
                            }
                          />
                        </Routes>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </ProtectedRoute>
            }
          />
        </Routes>
      </Box>
    </LocalizationProvider>
  );
};

export default Layout;
